import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { fetchGalleryByTitle, fetchServiceByTitle } from '../helpers/contentfulHelper';
import styles from '../styles/StairsPage.module.css';

const StairsPage = () => {
  const [stairImages, setStairImages] = useState([]);
  const [isGalleryLoading, setIsGalleryLoading] = useState(true);
  const [serviceCopy, setServiceCopy] = useState(null);
  const [galleryCaption, setGalleryCaption] = useState('Stairs');
  const [activeIndex, setActiveIndex] = useState(null);
  const hasLightbox = activeIndex !== null;

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const entry = await fetchGalleryByTitle('Stairs Gallery');
        const contentfulImages = entry?.fields?.images || [];
        const caption = entry?.fields?.caption;

        if (caption) {
          setGalleryCaption(caption);
        }

        const mapped = contentfulImages
          .map((img) => {
            const url = img?.fields?.file?.url;
            if (!url) return null;

            return {
              src: `https:${url}`,
              alt: img?.fields?.title || 'Stairs gallery image',
            };
          })
          .filter(Boolean);

        setStairImages(mapped);
      } finally {
        setIsGalleryLoading(false);
      }
    };

    loadGallery();
  }, []);

  useEffect(() => {
    const loadServiceCopy = async () => {
      const service = await fetchServiceByTitle('Stairs');
      if (service?.fields?.copy) {
        setServiceCopy(service.fields.copy);
      }
    };

    loadServiceCopy();
  }, []);

  const openLightbox = (index) => setActiveIndex(index);
  const closeLightbox = () => setActiveIndex(null);

  const goPrev = () => {
    setActiveIndex((prev) => {
      if (prev === null) return prev;
      return prev === 0 ? stairImages.length - 1 : prev - 1;
    });
  };

  const goNext = () => {
    setActiveIndex((prev) => {
      if (prev === null) return prev;
      return prev === stairImages.length - 1 ? 0 : prev + 1;
    });
  };

  return (
    <section className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <p className={styles.kicker}>Our Work</p>
          <h1>{galleryCaption}</h1>
          {serviceCopy ? (
            <div className={styles.headerCopy}>
              {documentToReactComponents(serviceCopy)}
            </div>
          ) : (
            <p>
              A curated selection of bespoke staircases, designed and built with
              traditional joinery methods for both heritage and modern homes.
            </p>
          )}
        </header>

        <div className={styles.imageGrid}>
          {isGalleryLoading &&
            Array.from({ length: 6 }).map((_, index) => (
              <div key={`stairs-skeleton-${index}`} className={styles.skeletonCard} />
            ))}

          {!isGalleryLoading &&
            stairImages.map((image, index) => (
              <button
                key={image.src}
                type="button"
                className={styles.imageButton}
                onClick={() => openLightbox(index)}
                aria-label={`Open image ${index + 1}`}
              >
                <img src={image.src} alt={image.alt} />
              </button>
            ))}

          {!isGalleryLoading && stairImages.length === 0 && (
            <p className={styles.emptyState}>Gallery images coming soon.</p>
          )}
        </div>

        <div className={styles.ctaWrap}>
          <h3>Discuss your staircase project</h3>
          <p>Share your plans and we&apos;ll help shape the right solution.</p>
          <Link to="/contact">Make an Enquiry</Link>
        </div>

        {hasLightbox && (
          <div className={styles.lightbox} role="dialog" aria-modal="true">
            <button
              type="button"
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={goPrev}
              aria-label="Previous image"
            >
              &#8249;
            </button>

            <div className={styles.lightboxInner}>
              <button
                type="button"
                className={styles.closeButton}
                onClick={closeLightbox}
                aria-label="Close gallery"
              >
                &#10005;
              </button>

              <img
                src={stairImages[activeIndex].src}
                alt={stairImages[activeIndex].alt}
                className={styles.lightboxImage}
              />

              <div className={styles.thumbRow}>
                {stairImages.map((image, index) => (
                  <button
                    key={`${image.src}-thumb`}
                    type="button"
                    className={`${styles.thumbButton} ${
                      index === activeIndex ? styles.thumbActive : ''
                    }`}
                    onClick={() => openLightbox(index)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={image.src} alt="" />
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={goNext}
              aria-label="Next image"
            >
              &#8250;
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default StairsPage;
